import { useState } from 'react';
import { useLocation } from 'react-router';
import { makeStyles } from '@mui/styles';
import { Link as RouterLink } from 'react-router';
import {
  Box,
  ButtonBase,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography
} from '@mui/material';
import ListItemIcon from '@mui/material/ListItemIcon';
import { NavCollapseIcon } from '../../../common/icons/NavCollapseIcon';
import { NavExpandIcon } from '../../../common/icons/NavExpandIcon';

import MenuLinks, { NavHeaderGroup, NavHeaderItem } from './config';
import AuthUserStore from '@/common/utils/AuthUser';
import { AuthUserRole } from '@/models/AuthUserModel';
import { Role } from '@/models/common';
import Activity from './Notifications';
import ClientSearch from './ClientSearch';
import TerminalStatus from './TerminalStatus';
import NavMenu from './NavMenu';

export const NavStyles = makeStyles(theme => ({
  navContainer: {
    height: '100vh',
    width: 60,
    zIndex: 10000,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#F5F5F5',
    transition: '225ms cubic-bezier(0, 0, 0.2, 1)',
    '&.expanded': {
      width: 240
    }
  },
  navHeaderContainer: {
    flexShrink: 0
  },
  navBodyContainer: {
    position: 'relative',
    flexGrow: 1,
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      display: 'none'
    },
    '-ms-overflow-style': 'none',
    'scrollbar-width': 'none'
  },
  navFooterContainer: {
    flexShrink: 0
  },
  navHeader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&.expanded': {
      justifyContent: 'space-between',
      width: '100%',
      padding: '8px 8px 0 8px'
    }
  },
  menuButton: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    textAlign: 'left',
    padding: 0,
    borderRadius: 6,
    '&:hover': {
      backgroundColor: '#EFEFEF'
    },
    '&.expanded': {
      height: 46
    }
  },
  logo: {
    marginTop: 8,
    marginRight: 16
  },
  userBox: {
    width: 32,
    height: 32,
    backgroundColor: '#ECECEC',
    color: '#999999',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #DADADA',
    borderRadius: 4,
    margin: '16px auto',
    '&.collapsed': {
      marginBottom: 0
    }
  },
  userInits: {
    fontSize: 14,
    fontWeight: 500
  },
  userBoxContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 8,
    '&.collapsed': {
      flexDirection: 'column'
    }
  },
  userBoxInfo: {
    marginLeft: 12
  },
  userName: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '16px',
    color: '#000000',
    marginTop: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '128px'
  },
  userStudio: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#777777'
  },
  userStudioCollapsed: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#aaaaaa',
    paddingTop: '4px',
    paddingBottom: '4px'
  },
  leftNavSearch: {
    paddingTop: '8px',
    width: 'calc(100% - 16px)',
    '& .MuiAutocomplete-noOptions': {
      display: 'none'
    }
  },
  groupHead: {},
  groupContent: {
    padding: 0
  },
  groupDivider: {
    margin: theme.spacing(0, 1, 2.25, 1),
    height: '14px'
  },
  navToggle: {
    height: 40,
    margin: theme.spacing(0.5),
    borderRadius: 6,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)'
    },
    '&.active': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)'
    }
  },
  linkButton: {
    height: 40,
    margin: theme.spacing(1),
    borderRadius: 6,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)'
    },
    '&.active': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)'
    }
  },
  linkHeader: {
    color: '#777777',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '12px',
    letterSpacing: '0.3px',
    paddingTop: '16px',
    paddingBottom: '6px'
  },
  linkIcon: {
    position: 'relative',
    color: '#444444',
    alignItems: 'center',
    minWidth: 0,
    justifyContent: 'center',
    '&.terminalStatus': {
      marginRight: 4
    }
  },
  linkText: {
    color: '#444444',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '16px',
    whiteSpace: 'nowrap',
    overflow: 'hidden'
  }
}));

const check_roles = (
  link: NavHeaderItem,
  storeId: number,
  roles: AuthUserRole[]
): boolean => {
  for (const userRole of roles) {
    try {
      const included = link.roles.includes(userRole.role as Role);
      if (included && userRole.store == null) {
        return true;
      } else if (
        included &&
        userRole.store != null &&
        userRole.store == storeId
      ) {
        return true;
      }
    } catch (e) {
      // nothing to do here
    }
  }
  return false;
};

const permissionFilter = (
  groups: NavHeaderGroup[],
  storeId: number | null,
  roles: AuthUserRole[]
): NavHeaderGroup[] => {
  if (storeId == null) {
    return [];
  }
  const result = [];
  const authUser = AuthUserStore.getAuthUser();
  groups.forEach(group => {
    const links = [];
    const items = group.items;
    items.forEach(item => {
      const hasRoleForStore = check_roles(item, storeId, roles);
      if (hasRoleForStore) {
        // TEMPORARY CHECK FOR GENERIC USERS AND STAFF MANAGEMENT
        if (!(item.display == 'Staff' && authUser?.isGeneric)) {
          links.push(item);
        }
      }
    });
    if (links.length > 0) {
      result.push({ ...group, items: links });
    }
  });
  return result;
};

const getActiveTag = (href, pathname) => {
  const length = href.split('/').length;
  let currentPath = '';
  const paths = pathname.split('/');
  for (let i = 1; i < length; i++) {
    const path = paths[i];
    currentPath = currentPath + '/' + path;
  }
  return currentPath === href ? 'active' : '';
};

function LeftNav(props) {
  const { isOpen, setIsOpen } = props;
  const classes = NavStyles();
  const location = useLocation();
  const roles = AuthUserStore.getRoles();
  const storeId = AuthUserStore.getStoreId();
  const governedNavLinks = permissionFilter(MenuLinks, storeId, roles);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const loggedInUser = AuthUserStore.getAuthUser();

  const userInitials =
    loggedInUser?.firstName && loggedInUser?.lastName
      ? (
          loggedInUser.firstName.slice(0, 1) + loggedInUser.lastName.slice(0, 1)
        ).toUpperCase()
      : (loggedInUser?.username?.slice(0, 1) || '').toUpperCase();

  const handleDrawerClose = () => {
    setIsOpen(!isOpen);
  };

  const handleMenuClick = event => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  return (
    <div className={`${classes.navContainer} ${isOpen ? 'expanded' : ''}`}>
      <div className={classes.navHeaderContainer}>
        {isOpen ? (
          <Box className={`${classes.navHeader} expanded`}>
            {/* The clickable user box */}
            <ButtonBase
              className={`${classes.menuButton} expanded`}
              onClick={handleMenuClick}
            >
              <Box className={classes.userBoxContainer}>
                <Box className={classes.userBox}>
                  <Typography variant="body1" className={classes.userInits}>
                    {userInitials}
                  </Typography>
                </Box>
                <Box className={classes.userBoxInfo}>
                  <Typography className={classes.userName}>
                    {loggedInUser?.username}
                  </Typography>
                  <Typography className={classes.userStudio}>
                    {loggedInUser?.store?.storeCode}
                  </Typography>
                </Box>
              </Box>
            </ButtonBase>
            <Box>
              <Tooltip title="Collapse" placement="right">
                <IconButton
                  className={classes.navToggle}
                  onClick={handleDrawerClose}
                >
                  <NavCollapseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ) : (
          <>
            <Box className={classes.navHeader}>
              <Tooltip title={'Expand'} placement="right">
                <IconButton
                  className={classes.linkButton}
                  onClick={e => {
                    e.stopPropagation(); // Prevents triggering the ButtonBase click event
                    handleDrawerClose();
                  }}
                >
                  {<NavExpandIcon />}
                </IconButton>
              </Tooltip>
            </Box>
            <Divider />
            <Box className={classes.navHeader}>
              <ButtonBase
                className={classes.menuButton}
                onClick={handleMenuClick}
              >
                <Box className={`${classes.userBoxContainer} collapsed`}>
                  <Box className={`${classes.userBox} collapsed`}>
                    <Typography className={classes.userInits}>
                      {userInitials}
                    </Typography>
                  </Box>
                  <Typography className={classes.userStudioCollapsed}>
                    {loggedInUser?.store?.storeCode}
                  </Typography>
                </Box>
              </ButtonBase>
            </Box>
            <Divider />
          </>
        )}
        <NavMenu
          anchorEl={menuAnchor}
          onClose={handleMenuClose}
          isNavOpen={isOpen}
        />
      </div>
      <div className={classes.navBodyContainer}>
        <List>
          <List className={classes.groupContent}>
            <ClientSearch isNavOpen={isOpen} setIsNavOpen={setIsOpen} />
            <Activity isOpen={isOpen} />
          </List>
          {governedNavLinks.map(group => {
            return (
              <div key={group.display}>
                <Box className={classes.groupHead}>
                  {isOpen ? (
                    <Typography className={classes.linkHeader} sx={{ px: 2 }}>
                      {group.display}
                    </Typography>
                  ) : (
                    <Divider className={classes.groupDivider} />
                  )}
                </Box>
                <List className={classes.groupContent}>
                  {group?.items.map((item, i) => {
                    const menuId = item.display.replace(' ', '');
                    return (
                      <Tooltip
                        title={isOpen ? '' : item.display}
                        placement="right"
                        key={i}
                      >
                        <ListItem
                          key={menuId}
                          disablePadding
                          sx={{ display: 'block' }}
                        >
                          <ListItemButton
                            component={RouterLink}
                            className={`${classes.linkButton} ${getActiveTag(
                              item.href,
                              location.pathname
                            )}`}
                            to={item.href}
                            sx={{
                              justifyContent: isOpen ? 'initial' : 'center'
                            }}
                          >
                            <ListItemIcon
                              className={classes.linkIcon}
                              sx={{ mr: isOpen ? 2 : 'auto' }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={isOpen ? item.display : ''}
                              classes={{ primary: classes.linkText }}
                              sx={{ opacity: isOpen ? 1 : 0 }}
                            />
                          </ListItemButton>
                        </ListItem>
                      </Tooltip>
                    );
                  })}
                </List>
              </div>
            );
          })}
        </List>
      </div>
      <div className={classes.navFooterContainer}>
        <Divider />
        <List className={classes.groupContent}>
          <TerminalStatus isOpen={isOpen} getActiveTag={getActiveTag} />
        </List>
      </div>
    </div>
  );
}
export default LeftNav;
